import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User as UserIcon, ShieldCheck, FileSpreadsheet, Folder } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";

const navLink = ({ isActive }) =>
    `px-3 py-2 ui-heading text-sm transition-colors duration-150 ${
        isActive ? "text-ink" : "text-ink-soft hover:text-cobalt"
    }`;

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    // App-mode: a user who's signed in and paid (or admin) is in "the app" — hide
    // all marketing nav and just show the work surface (Editor / Files / Account).
    const isAppMode = !!user && (user.is_paid || user.role === "admin");

    return (
        <header
            className="sticky top-0 z-40 border-b border-black/10 backdrop-blur-xl bg-white/70"
            data-testid="navbar"
        >
            <div className="mx-auto max-w-7xl px-4 md:px-8 flex items-center justify-between h-16">
                <Link to={isAppMode ? "/app" : "/"} className="flex items-center" data-testid="navbar-home-link">
                    <Logo />
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-1">
                    {isAppMode ? (
                        <>
                            <NavLink to="/app" className={navLink} data-testid="nav-app">
                                <span className="inline-flex items-center gap-1.5"><FileSpreadsheet size={14} /> Editor</span>
                            </NavLink>
                            <NavLink to="/files" className={navLink} data-testid="nav-files">
                                <span className="inline-flex items-center gap-1.5"><Folder size={14} /> My files</span>
                            </NavLink>
                            {user?.role === "admin" && (
                                <NavLink to="/admin" className={navLink} data-testid="nav-admin">Admin</NavLink>
                            )}
                            {user?.role === "admin" && (
                                <NavLink to="/admin/inbox" className={navLink} data-testid="nav-inbox">Inbox</NavLink>
                            )}
                        </>
                    ) : (
                        <>
                            <NavLink to="/" end className={navLink} data-testid="nav-home">Home</NavLink>
                            <NavLink to="/csv-viewer" className={navLink} data-testid="nav-viewer">Viewer</NavLink>
                            <NavLink to="/csv-editor" className={navLink} data-testid="nav-editor-page">Editor</NavLink>
                            <NavLink to="/csv-use-cases" className={navLink} data-testid="nav-use">Use cases</NavLink>
                            <NavLink to="/blog" className={navLink} data-testid="nav-blog">Blog</NavLink>
                            <NavLink to="/pricing" className={navLink} data-testid="nav-pricing">Pricing</NavLink>
                            {user && (
                                // Logged in but not paid — give them a path to the editor (which gates them)
                                <NavLink to="/app" className={navLink} data-testid="nav-app">App</NavLink>
                            )}
                        </>
                    )}
                </nav>

                {/* Right side */}
                <div className="hidden md:flex items-center gap-2">
                    {!user ? (
                        <>
                            <Link to="/login" className="px-4 py-2 ui-heading text-sm text-ink hover:text-cobalt" data-testid="navbar-login">
                                Sign in
                            </Link>
                            <Link
                                to="/signup"
                                className="btn-brutal px-4 py-2 bg-ink text-white ui-heading text-sm font-semibold border border-ink"
                                data-testid="navbar-signup"
                            >
                                Try Pro →
                            </Link>
                        </>
                    ) : (
                        <div className="flex items-center gap-3">
                            {user.role === "admin" && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 border border-ink/20 ui-heading text-xs">
                                    <ShieldCheck size={12} /> ADMIN
                                </span>
                            )}
                            <Link to="/account" className="flex items-center gap-2 ui-heading text-sm text-ink hover:text-cobalt" data-testid="navbar-account">
                                <UserIcon size={14} /> {user.name}
                            </Link>
                            <button onClick={handleLogout} className="p-2 border border-black/10 hover:border-cobalt" data-testid="navbar-logout" aria-label="Sign out">
                                <LogOut size={14} />
                            </button>
                        </div>
                    )}
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    className="md:hidden p-2 border border-black/10"
                    aria-label="Toggle menu"
                    data-testid="navbar-mobile-toggle"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-black/10 bg-white" data-testid="mobile-menu">
                    <div className="flex flex-col p-4 gap-1">
                        {isAppMode ? (
                            <>
                                <NavLink to="/app" className={navLink} onClick={() => setOpen(false)}>Editor</NavLink>
                                <NavLink to="/files" className={navLink} onClick={() => setOpen(false)}>My files</NavLink>
                                <NavLink to="/account" className={navLink} onClick={() => setOpen(false)}>Account</NavLink>
                                {user?.role === "admin" && <NavLink to="/admin" className={navLink} onClick={() => setOpen(false)}>Admin</NavLink>}
                                {user?.role === "admin" && <NavLink to="/admin/inbox" className={navLink} onClick={() => setOpen(false)}>Inbox</NavLink>}
                            </>
                        ) : (
                            <>
                                <NavLink to="/" end className={navLink} onClick={() => setOpen(false)}>Home</NavLink>
                                <NavLink to="/csv-viewer" className={navLink} onClick={() => setOpen(false)}>CSV Viewer</NavLink>
                                <NavLink to="/csv-editor" className={navLink} onClick={() => setOpen(false)}>CSV Editor</NavLink>
                                <NavLink to="/csv-use-cases" className={navLink} onClick={() => setOpen(false)}>Use cases</NavLink>
                                <NavLink to="/blog" className={navLink} onClick={() => setOpen(false)}>Blog</NavLink>
                                <NavLink to="/pricing" className={navLink} onClick={() => setOpen(false)}>Pricing</NavLink>
                                <NavLink to="/about" className={navLink} onClick={() => setOpen(false)}>About</NavLink>
                                <NavLink to="/faq" className={navLink} onClick={() => setOpen(false)}>FAQ</NavLink>
                                {user && <NavLink to="/app" className={navLink} onClick={() => setOpen(false)}>Editor</NavLink>}
                                {user && <NavLink to="/account" className={navLink} onClick={() => setOpen(false)}>Account</NavLink>}
                            </>
                        )}
                        {!user ? (
                            <>
                                <Link to="/login" className="mt-2 px-4 py-2 ui-heading text-sm border border-black/10 text-center" onClick={() => setOpen(false)}>Sign in</Link>
                                <Link to="/signup" className="px-4 py-2 ui-heading text-sm bg-ink text-white text-center" onClick={() => setOpen(false)}>Try Pro</Link>
                            </>
                        ) : (
                            <button onClick={() => { setOpen(false); handleLogout(); }} className="mt-2 px-4 py-2 ui-heading text-sm border border-black/10 text-left">Sign out</button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
